// TODO:
// Add inset to viewport listener - to avoid overlays even when in negative scroll
// positions... is this principled?

// Should instead we only use scrollTop and... top of content?
// Should we just return the "real" top?
// We are.... so we need to actually avoid something else
// We can't clamp then to 0 - we need to clamp to the inset

export type ViewportListenerCallback = (
  scrollY: number,
  scrollX: number,
  viewHeight: number,
  viewWidth: number,
  insetTop: number,
) => void;

export class ViewportObserver {
  private readonly listeners: ViewportListenerCallback[] = [];
  private readonly listenerScrolls: ViewportListenerCallback[] = [];

  private _iframed = false;
  private _content: HTMLElement | null = null;

  private scrollUpdatePending = false;

  private _viewWidth = window.innerWidth;
  private _viewHeight = window.innerHeight;
  private _scrollY = window.scrollY;
  private _scrollX = window.scrollX;

  private _insetTop = 0;
  private _insetBottom = 0;

  public get viewWidth() {
    return this._viewWidth;
  }

  public get viewHeight() {
    return this._viewHeight;
  }

  public get scrollY() {
    return this._scrollY;
  }

  public get scrollX() {
    return this._scrollX;
  }

  public get iframed() {
    return this._iframed;
  }

  public static shared = new ViewportObserver();

  constructor() {
    this._iframed = window.parent !== window;

    if (this.iframed) {
      window.addEventListener("message", this.onMessageHandler);
    } else {
      window.addEventListener("scroll", this.onScrollHandler);
      this._viewWidth = window.innerWidth;
      this._viewHeight = window.innerHeight;
      this._scrollY = window.scrollY;
      this._scrollX = window.scrollX;
    }
  }

  private onMessageHandler = (event: MessageEvent) => {
    if (!event.data) return;

    const message = event.data;
    if (!message) return;

    if (message.type !== "sfml-viewport") return;

    this._viewWidth = message.data.width;
    this._viewHeight = message.data.height;
    this._scrollY = message.data.scrollY;
    this._scrollX = message.data.scrollX;

    this._insetTop = message.data.insetTop || 0;
    this._scrollY += this._insetTop;
    this._viewHeight -= this._insetTop;

    this._insetBottom = message.data.insetBottom || 0;
    this._viewHeight -= this._insetBottom;

    if (message.data.scroll) this.notifyScroll();
    else this.notify();
  };

  private onScrollHandler = (event: Event) => {
    this._viewWidth = window.innerWidth;
    this._viewHeight = window.innerHeight;
    this._scrollY = window.scrollY;
    this._scrollX = window.scrollX;

    if (this.scrollUpdatePending) return;

    window.requestAnimationFrame(() => {
      this.notifyScroll();
      this.scrollUpdatePending = false;
    });
    this.scrollUpdatePending = true;
  };

  public refresh() {
    if (!this._iframed) {
      this._viewWidth = window.innerWidth;
      this._viewHeight = window.innerHeight;
      this._scrollY = window.scrollY;
      this._scrollX = window.scrollX;
    }
  }

  public getBoundingClientRect(element: Element): ClientRect {
    const rect = element.getBoundingClientRect();

    if (!this.iframed) return rect;

    return {
      bottom: rect.bottom - this._scrollY,
      height: rect.height,
      left: rect.left - this._scrollX,
      right: rect.right - this._scrollX,
      top: rect.top - this._scrollY,
      width: rect.width,
    };
  }

  public notify() {
    for (let listener of this.listeners) {
      listener(this._scrollY, this._scrollX, this._viewHeight, this._viewWidth, this._insetTop);
    }
  }

  public notifyScroll() {
    this.notify();
    for (let listener of this.listenerScrolls) {
      listener(this._scrollY, this._scrollX, this._viewHeight, this._viewWidth, this._insetTop);
    }
  }

  public listen(callback: ViewportListenerCallback) {
    this.listeners.push(callback);
  }

  public listenScroll(callback: ViewportListenerCallback) {
    this.listenerScrolls.push(callback);
  }

  public resize() {
    const newScrollY = this._scrollY / this._viewWidth * window.innerWidth;

    if (!this._content) return;

    if (this.iframed) {
      const contentHeight = document.body.clientHeight;
      const contentWidth = this._content.offsetWidth;
      const message = {
        type: "sfml-resize",
        data: {
          contentHeight,
          contentWidth,
        },
      };

      this._content.dispatchEvent(
        new CustomEvent("sfml-resize", {
          bubbles: true,
          detail: {
            contentHeight,
            contentWidth,
          },
        }),
      );

      window.parent.postMessage(message, "*");
    }

    this.scrollTo(this._content, this._scrollX, newScrollY);
  }

  public ready(content: HTMLElement) {
    this._content = content;

    if (this.iframed) {
      const contentHeight = document.body.clientHeight;
      const contentWidth = content.offsetWidth;
      const message = {
        type: "sfml-ready",
        data: {
          contentHeight,
          contentWidth,
        },
      };

      this._content.dispatchEvent(
        new CustomEvent("sfml-ready", {
          bubbles: true,
          detail: {
            contentHeight,
            contentWidth,
          },
        }),
      );

      window.parent.postMessage(message, "*");
    }
  }

  public scrollIntoView(element: HTMLElement) {
    if (this.iframed) {
      const x = 0;
      let y = element.offsetTop;
      let elem = element;
      /* tslint:disable:no-conditional-assignment */
      while ((elem = elem.offsetParent as HTMLElement)) {
        y += elem.offsetTop;
      }

      const message = {
        type: "sfml-scroll-to",
        data: {
          x,
          y,
        },
      };

      element.dispatchEvent(
        new CustomEvent("sfml-scroll-to", {
          bubbles: true,
          detail: {
            x,
            y,
          },
        }),
      );

      window.parent.postMessage(message, "*");
    } else {
      element.scrollIntoView(true);
    }
  }

  public scrollTo(element: HTMLElement, x: number, y: number) {
    if (this.iframed) {
      const message = {
        type: "sfml-scroll-to",
        data: {
          x,
          y,
        },
      };

      element.dispatchEvent(
        new CustomEvent("sfml-scroll-to", {
          bubbles: true,
          detail: {
            x,
            y,
          },
        }),
      );

      window.parent.postMessage(message, "*");
    } else {
      window.scrollTo(x, y);
    }
  }

  public unlisten(callback: ViewportListenerCallback) {
    for (let i = 0, n = this.listeners.length; i < n; ++i) {
      const listener = this.listeners[i];
      if (listener === callback) {
        this.listeners.splice(i, 1);
        return;
      }
    }
  }

  public unlistenScroll(callback: ViewportListenerCallback) {
    for (let i = 0, n = this.listenerScrolls.length; i < n; ++i) {
      const listener = this.listenerScrolls[i];
      if (listener === callback) {
        this.listenerScrolls.splice(i, 1);
        return;
      }
    }
  }

  public disconnect(): void {
    if (this.iframed) window.removeEventListener("message", this.onMessageHandler);
    else window.removeEventListener("scroll", this.onScrollHandler);
  }
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/viewport.ts